/**
 * HedgingTab - Create and manage fuel hedging contracts
 */

import React, { useState, useMemo } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Button } from "@/components/ui/button";
import {
  getHedgeDurations,
  calculateHedgePremium,
  calculateBreakEven,
  createHedgeContract,
  formatFuelPrice,
  formatGallons,
} from "../../systems/FuelSystem.js";

const GALLON_PRESETS = [50000, 100000, 250000, 500000, 1000000];
const HEDGE_DURATIONS = getHedgeDurations();

function HedgeCard({ hedge, spotPrice }) {
  const [expanded, setExpanded] = useState(false);

  // Profit/loss calculation
  const priceGap = spotPrice - hedge.lockPrice;
  const totalValue = hedge.gallonsRemaining * priceGap;
  const isProfitable = totalValue > 0;

  // Status badge
  const statusStyles = {
    pending: { bg: "#3d3d1a", border: "#eab308", text: "#facc15" },
    active: { bg: "#1a3d1a", border: "#22c55e", text: "#4ade80" },
    expired: { bg: "#2a2a2a", border: "#525252", text: "#737373" },
  };
  const style = statusStyles[hedge.status] || statusStyles.expired;

  return (
    <div
      style={{
        backgroundColor: "#111",
        borderRadius: "8px",
        border: `1px solid ${hedge.status === "active" ? "#22c55e40" : "#1a1a1a"}`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ color: "#e5e5e5", fontWeight: "600", fontSize: "14px" }}>
              HEDGE #{hedge.id.slice(-6).toUpperCase()}
            </span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "bold",
                backgroundColor: style.bg,
                border: `1px solid ${style.border}`,
                color: style.text,
              }}
            >
              {hedge.status.toUpperCase()}
            </span>
          </div>

          <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>LOCK PRICE</span>
              <div style={{ color: "#60ffd0", fontFamily: "monospace", fontWeight: "bold", fontSize: "14px" }}>
                ${hedge.lockPrice.toFixed(2)}
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>REMAINING</span>
              <div style={{ color: "#e5e5e5", fontSize: "14px" }}>
                {formatGallons(hedge.gallonsRemaining)}
              </div>
            </div>
            <div>
              <span style={{ color: "#525252", fontSize: "10px" }}>MONTHS LEFT</span>
              <div style={{ color: hedge.monthsRemaining <= 1 ? "#eab308" : "#e5e5e5", fontSize: "14px" }}>
                {hedge.monthsRemaining}
              </div>
            </div>
            {hedge.status === "active" && (
              <div>
                <span style={{ color: "#525252", fontSize: "10px" }}>VALUE</span>
                <div
                  style={{
                    color: isProfitable ? "#22c55e" : "#ef4444",
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                >
                  {isProfitable ? "+" : ""}${(totalValue / 1000).toFixed(1)}K
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ color: "#525252", fontSize: "12px" }}>
          {expanded ? "▼" : "▶"}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 16px 16px", borderTop: "1px solid #1a1a1a", paddingTop: "16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>PREMIUM PAID</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>
                ${(hedge.premium / 1000).toFixed(1)}K
              </div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>BREAK-EVEN</div>
              <div style={{ color: "#eab308", fontSize: "13px", fontFamily: "monospace" }}>
                ${hedge.breakEvenPrice.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ color: "#525252", fontSize: "10px" }}>ORIGINAL GAL</div>
              <div style={{ color: "#e5e5e5", fontSize: "13px" }}>
                {formatGallons(hedge.gallons)}
              </div>
            </div>
          </div>

          {hedge.status === "active" && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: isProfitable ? "#0a1a0a" : "#1a0a0a",
                borderRadius: "6px",
                border: `1px solid ${isProfitable ? "#22c55e40" : "#ef444440"}`,
              }}
            >
              <div style={{ color: isProfitable ? "#22c55e" : "#ef4444", fontSize: "12px" }}>
                {isProfitable
                  ? `Saving ${formatFuelPrice(priceGap)}/gal vs spot. Total potential savings: $${(totalValue / 1000).toFixed(1)}K`
                  : `Lock price is ${formatFuelPrice(Math.abs(priceGap))}/gal above spot. Consider using for future price protection.`}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HedgeBuilder({ spotPrice, cash, macroState, onCreateHedge }) {
  const [lockPrice, setLockPrice] = useState(spotPrice);
  const [gallons, setGallons] = useState(100000);
  const [durationKey, setDurationKey] = useState("SHORT");

  const duration = HEDGE_DURATIONS[durationKey];

  const premium = useMemo(() => {
    return calculateHedgePremium(gallons, lockPrice, duration.months, macroState);
  }, [gallons, lockPrice, duration.months, macroState]);

  const breakEven = useMemo(() => {
    return calculateBreakEven(lockPrice, premium, gallons);
  }, [lockPrice, premium, gallons]);

  const canAfford = cash >= premium;
  const volatility = macroState?.global?.OPVI || 0.35;
  const volatilityLevel = volatility > 0.6 ? "HIGH" : volatility > 0.35 ? "MODERATE" : "LOW";

  const handleCreate = () => {
    if (!canAfford) return;
    onCreateHedge({
      gallons,
      lockPrice,
      durationMonths: duration.months,
      macroState,
    });
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#0a0a0a",
        borderRadius: "8px",
        border: "1px solid #1a1a1a",
      }}
    >
      <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "16px" }}>
        CREATE NEW HEDGE
      </div>

      {/* Lock Price */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>
          LOCK PRICE ($/gal)
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            type="number"
            value={lockPrice}
            onChange={(e) => setLockPrice(parseFloat(e.target.value) || spotPrice)}
            step="0.05"
            min="2.00"
            max="6.00"
            style={{
              flex: 1,
              padding: "10px 12px",
              backgroundColor: "#111",
              border: "1px solid #333",
              borderRadius: "6px",
              color: "#60ffd0",
              fontSize: "16px",
              fontFamily: "monospace",
              fontWeight: "bold",
            }}
          />
          <button
            onClick={() => setLockPrice(spotPrice)}
            style={{
              padding: "10px 16px",
              backgroundColor: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: "6px",
              color: "#737373",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            SPOT (${spotPrice.toFixed(2)})
          </button>
        </div>
      </div>

      {/* Gallons */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>
          GALLONS
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {GALLON_PRESETS.map((preset) => (
            <button
              key={preset}
              onClick={() => setGallons(preset)}
              style={{
                padding: "8px 12px",
                backgroundColor: gallons === preset ? "#1a3d1a" : "#1a1a1a",
                border: `1px solid ${gallons === preset ? "#22c55e" : "#333"}`,
                borderRadius: "6px",
                color: gallons === preset ? "#4ade80" : "#a1a1a1",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              {formatGallons(preset)}
            </button>
          ))}
        </div>
        <input
          type="range"
          min="10000"
          max="2000000"
          step="10000"
          value={gallons}
          onChange={(e) => setGallons(parseInt(e.target.value))}
          style={{
            width: "100%",
            marginTop: "8px",
            accentColor: "#60ffd0",
          }}
        />
        <div style={{ color: "#e5e5e5", fontSize: "14px", marginTop: "4px", textAlign: "center" }}>
          {gallons.toLocaleString()} gallons
        </div>
      </div>

      {/* Duration */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>
          DURATION
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {Object.entries(HEDGE_DURATIONS).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setDurationKey(key)}
              style={{
                flex: 1,
                padding: "10px",
                backgroundColor: durationKey === key ? "#1a3d1a" : "#1a1a1a",
                border: `1px solid ${durationKey === key ? "#22c55e" : "#333"}`,
                borderRadius: "6px",
                color: durationKey === key ? "#4ade80" : "#a1a1a1",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              {config.label}
              {config.premiumMultiplier < 1 && (
                <div style={{ fontSize: "10px", color: "#22c55e", marginTop: "2px" }}>
                  {((1 - config.premiumMultiplier) * 100).toFixed(0)}% off
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "12px",
          padding: "16px",
          backgroundColor: "#111",
          borderRadius: "6px",
          marginBottom: "16px",
        }}
      >
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>PREMIUM COST</div>
          <div
            style={{
              color: canAfford ? "#e5e5e5" : "#ef4444",
              fontSize: "16px",
              fontFamily: "monospace",
              fontWeight: "bold",
            }}
          >
            ${(premium / 1000).toFixed(1)}K
          </div>
        </div>
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>BREAK-EVEN</div>
          <div style={{ color: "#eab308", fontSize: "16px", fontFamily: "monospace", fontWeight: "bold" }}>
            ${breakEven.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>VOLATILITY</div>
          <div
            style={{
              color: volatilityLevel === "HIGH" ? "#ef4444" : volatilityLevel === "MODERATE" ? "#eab308" : "#22c55e",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {volatilityLevel}
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div
        style={{
          padding: "12px",
          backgroundColor: "#0d1a0d",
          border: "1px solid #22c55e30",
          borderRadius: "6px",
          marginBottom: "16px",
        }}
      >
        <div style={{ color: "#a1a1a1", fontSize: "12px", lineHeight: "1.5" }}>
          Spot must exceed <strong style={{ color: "#eab308" }}>${breakEven.toFixed(2)}</strong> for this hedge to save money.
          {lockPrice < spotPrice && (
            <span style={{ color: "#22c55e" }}>
              {" "}You're locking in ${(spotPrice - lockPrice).toFixed(2)} below current spot!
            </span>
          )}
          {lockPrice > spotPrice && (
            <span style={{ color: "#ef4444" }}>
              {" "}Warning: Lock price is above current spot.
            </span>
          )}
        </div>
      </div>

      {/* Create button */}
      <Button
        onClick={handleCreate}
        disabled={!canAfford}
        style={{
          width: "100%",
          padding: "14px",
          backgroundColor: canAfford ? "#1a3d1a" : "#1a1a1a",
          borderColor: canAfford ? "#22c55e" : "#333",
          color: canAfford ? "#4ade80" : "#525252",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {canAfford ? "CREATE HEDGE" : `INSUFFICIENT FUNDS (Need $${(premium / 1000).toFixed(1)}K)`}
      </Button>
    </div>
  );
}

export function HedgingTab({ fuelState, cash, macroState }) {
  const { worldState, updateWorldState } = useWorldState();
  const hedges = fuelState?.hedges || [];
  const spotPrice = fuelState?.spotPrice || 2.95;

  const activeHedges = hedges.filter((h) => h.status === "active");
  const pendingHedges = hedges.filter((h) => h.status === "pending");

  const handleCreateHedge = (params) => {
    const result = createHedgeContract(
      fuelState,
      params,
      cash,
      worldState.gameTime || new Date().toISOString(),
      worldState.currentMonth || 1
    );

    if (result.success) {
      updateWorldState((prev) => ({
        ...prev,
        fuel: result.newFuelState,
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - result.cost,
        },
      }));
    }
  };

  // Calculate total hedged value
  const totalHedgedGallons = [...activeHedges, ...pendingHedges].reduce(
    (sum, h) => sum + h.gallonsRemaining,
    0
  );

  return (
    <div>
      {/* Summary stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "12px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#111",
            borderRadius: "8px",
            border: "1px solid #1a1a1a",
          }}
        >
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>ACTIVE HEDGES</div>
          <div style={{ color: "#60ffd0", fontSize: "24px", fontWeight: "bold" }}>
            {activeHedges.length}
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#111",
            borderRadius: "8px",
            border: "1px solid #1a1a1a",
          }}
        >
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>HEDGED GALLONS</div>
          <div style={{ color: "#e5e5e5", fontSize: "24px", fontWeight: "bold" }}>
            {formatGallons(totalHedgedGallons)}
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#111",
            borderRadius: "8px",
            border: "1px solid #1a1a1a",
          }}
        >
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>TOTAL SAVED</div>
          <div style={{ color: "#22c55e", fontSize: "24px", fontWeight: "bold" }}>
            ${((fuelState?.hedgeSavings || 0) / 1000).toFixed(1)}K
          </div>
        </div>
      </div>

      {/* Hedge builder */}
      <div style={{ marginBottom: "24px" }}>
        <HedgeBuilder
          spotPrice={spotPrice}
          cash={cash}
          macroState={macroState}
          onCreateHedge={handleCreateHedge}
        />
      </div>

      {/* Active hedges */}
      {activeHedges.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ marginBottom: "12px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
            ACTIVE HEDGES ({activeHedges.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {activeHedges.map((hedge) => (
              <HedgeCard key={hedge.id} hedge={hedge} spotPrice={spotPrice} />
            ))}
          </div>
        </div>
      )}

      {/* Pending hedges */}
      {pendingHedges.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ marginBottom: "12px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
            PENDING (Activates Next Month)
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {pendingHedges.map((hedge) => (
              <HedgeCard key={hedge.id} hedge={hedge} spotPrice={spotPrice} />
            ))}
          </div>
        </div>
      )}

      {/* Info footer */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "#0a0a0a",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
        }}
      >
        <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>
          ABOUT HEDGING
        </div>
        <div style={{ color: "#a1a1a1", fontSize: "13px", lineHeight: "1.6" }}>
          Hedging lets you lock in a future fuel price by paying a premium upfront.
          If spot prices rise above your break-even price, you save money.
          If prices stay low, you've paid extra for price insurance.
          Higher market volatility increases premium costs but also increases the value of protection.
        </div>
      </div>
    </div>
  );
}

export default HedgingTab;
